import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  connectRequestsToRequest,
  disconnectRequests,
} from '../../../../../api'
import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../../constants/common'
import { REQUEST_PROFILE_CHANGE_TYPE } from '../../../../../constants/request'
import { useNotification } from '../../../../../hooks'
import { getItemsIds } from '../../../../../utils/common'
import { ConfirmationModal } from '../../../../Confirmation'
import {
  AttachServiceRequestsModal,
  getRelatedRequestsLimitErrorMeta,
} from '../../../../Request'
import {
  useConnectedRequestsPreview,
  useRequestProfileContext,
} from '../../../RequestProfile.hooks'
import {
  REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_ACTION,
  REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_DEATTACH_CONFIRMATION_KEY,
  REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_REMOVE_ATTACH_CONFIRMATION_KEY,
} from '../../RequestProfileTabAttachedRequests.constants'
import { getRequestProfileTabAttachedRequestsNotificationTitle } from '../../RequestProfileTabAttachedRequests.utils'

export function useRequestProfileTabAttachedRequestsActions() {
  const { t } = useTranslation()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const { handleCloseModal, handleReloadAfterChange, requestData, setModal } =
    useRequestProfileContext()

  const requestId = requestData?.id

  const [isPerformingAction, setPerformingAction] = useState(false)

  async function handleReloadAfterSuccess() {
    return handleReloadAfterChange(REQUEST_PROFILE_CHANGE_TYPE.relatedRequests)
  }

  async function handleMassEditSuccess() {
    return handleReloadAfterChange(
      REQUEST_PROFILE_CHANGE_TYPE.providers,
      REQUEST_PROFILE_CHANGE_TYPE.relatedRequests
    )
  }

  const { handleClickOpenMassEdit: handleClickMassEdit } =
    useConnectedRequestsPreview({
      requestData,
      onCloseModal: handleCloseModal,
      onMassEditSuccess: handleMassEditSuccess,
      onSetModal: setModal,
    })

  async function handleAttachRequests({ selected, status }) {
    return connectRequestsToRequest({
      data: {
        ...(status ? { restore_archived_with_status: status } : EMPTY_OBJECT),
        requests: getItemsIds(selected),
      },
      id: requestId,
    })
      .then(() => {
        handleAddSuccessNotification({
          title: getRequestProfileTabAttachedRequestsNotificationTitle({
            action: REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_ACTION.attach,
            count: selected.length,
            t,
          }),
        })

        handleReloadAfterSuccess()
        handleCloseModal()
      })
      .catch(error => {
        if (getRelatedRequestsLimitErrorMeta(error)) {
          throw error
        }

        handleAddErrorNotification('Error.CommonApiRequestFailKey')
        handleCloseModal()
      })
  }

  function handleClickAttach() {
    setModal(
      <AttachServiceRequestsModal
        multipleSelection
        showLimitNotification
        excludeIds={[
          ...(requestData?.related_request_ids || EMPTY_LIST),
          requestId,
        ]}
        onAttach={handleAttachRequests}
        onClose={handleCloseModal}
      />
    )
  }

  async function handleDisconnect(requests) {
    setPerformingAction(true)

    return disconnectRequests({ data: { requests }, id: requestId })
      .then(() => {
        handleAddSuccessNotification({
          title: getRequestProfileTabAttachedRequestsNotificationTitle({
            action: REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_ACTION.deattach,
            count: requests.length,
            t,
          }),
        })

        handleReloadAfterSuccess()
      })
      .catch(() => {
        handleAddErrorNotification('Error.CommonApiRequestFailKey')
      })
      .finally(() => {
        setPerformingAction(false)
        handleCloseModal()
      })
  }

  function handleClickDeattach() {
    function handleSubmit() {
      handleCloseModal()
      handleDisconnect([requestId])
    }

    setModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: t(
            REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_DEATTACH_CONFIRMATION_KEY.submitText
          ),
        }}
        description={t(
          REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_DEATTACH_CONFIRMATION_KEY.description
        )}
        title={t(
          REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_DEATTACH_CONFIRMATION_KEY.title
        )}
        onCancel={handleCloseModal}
        onSubmit={handleSubmit}
      />
    )
  }

  function handleClickRemoveAttach(selectedIds) {
    function handleSubmit() {
      handleCloseModal()
      handleDisconnect(selectedIds)
    }

    setModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: t(
            REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_REMOVE_ATTACH_CONFIRMATION_KEY.submitText
          ),
        }}
        description={t(
          REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_REMOVE_ATTACH_CONFIRMATION_KEY.description,
          { count: selectedIds.length }
        )}
        title={t(
          REQUEST_PROFILE_TAB_ATTACHED_REQUESTS_REMOVE_ATTACH_CONFIRMATION_KEY.title
        )}
        onCancel={handleCloseModal}
        onSubmit={handleSubmit}
      />
    )
  }

  return {
    handleClickAttach,
    handleClickDeattach,
    handleClickMassEdit,
    handleClickRemoveAttach,
    isPerformingAction,
  }
}
